import { Moment } from 'moment'
import {
  Action,
  AsyncState,
  loadingAsyncState,
  none,
  Option,
  some
} from 'widgets-for-react'
import { Merchant } from '../../types/online_payment_platform'
import {
  exportSettlementSpecificationsToCsv,
  getSettlements,
  getSettlementSpecifications
} from './settlementsApi'

export type PagedSettlements = {
  page: number
  last_page: number
  settlements: Settlement[]
}

export type Settlement = {
  uid: string
  amount_paid: number
  amount_payable: number
  period_start: Moment
  period_end: Moment
  created: Moment
  updated: Moment
  status: string
  paid?: Moment
  total_number_of_transactions: number
  number_of_transactions: number
  number_of_refunds: number
  number_of_withdrawals: number
  number_of_mandates: number
  number_of_internal_transfers: number
  number_of_multi_transactions: number
  total_volume: number
  transaction_volume: number
  refund_volume: number
  withdrawal_volume: number
  mandate_volume: number
  internal_transfer_volume: number
  multi_transaction_volume: number
  total_transaction_costs: number
  transaction_costs: number
  refund_costs: number
  withdrawal_costs: number
  mandate_costs: number
  internal_transfer_costs: number
  multi_transaction_costs: number
  total_order_fees: number
  total_refund_fees: number
  total_gateway_fees: number
  total_amount: number
  payout_type: string
}

export interface SettlementSpecification {
  uid: string
  object: string
  created: Moment
  updated: Moment
  source: Merchant
  status: string
  paid?: Moment
  period_start: Moment
  period_end: Moment
  total_number_of_transactions: number
  number_of_transactions: number
  number_of_refunds: number
  number_of_withdrawals: number
  number_of_mandates: number
  number_of_internal_transfers: number
  number_of_multi_transactions: number
  total_volume: number
  transaction_volume: number
  refund_volume: number
  withdrawal_volume: number
  mandate_volume: number
  internal_transfer_volume: number
  multi_transaction_volume: number
  total_transaction_costs: number
  transaction_costs: number
  refund_costs: number
  withdrawal_costs: number
  mandate_costs: number
  internal_transfer_costs: number
  multi_transaction_costs: number
  total_order_fees: number
  total_refund_fees: number
  total_gateway_fees: number
  total_amount: number
  amount_paid: number
  amount_payable: number
}

export type SettlementSpecificationWithUserIds = {
  specification: SettlementSpecification
  user_id?: number
}
export type Props = {}
export type OpenSettlement = { uid: string }

export type State =
  | {
      kind: 'loading'
      overview: AsyncState<PagedSettlements>
    }
  | {
      kind: 'overview'
      overview: PagedSettlements
      openSettlement: Option<OpenSettlement>
      settlementExport: Option<{
        settlement: Settlement
        specifications: AsyncState<SettlementSpecificationWithUserIds[]>
      }>
    }

export type LayoutProps = {
  originalProps: Props
  state: State
  toggleSettlement: (_: Settlement) => void
  onPageChange: (_: number) => void
  onSettlementExport: (_: Settlement) => void
  isExporting: () => boolean
  isExportingSettlement: (_: Settlement) => boolean
}

export const initialState = (): State => ({
  kind: 'loading',
  overview: loadingAsyncState(_ => getSettlements(1))
})

export const valueOrNull = <a>(option: Option<a>): a =>
  option.v.kind === 'r' ? option.v.v : null

export const settlementsOverviewUpdaters = {
  onOverviewUpdate: (
    updater: Action<AsyncState<PagedSettlements>>
  ): Action<State> => s => {
    if (s.kind !== 'loading') {
      return s
    }
    const newOverview = updater(s.overview)

    return newOverview.kind === 'loaded'
      ? {
          kind: 'overview',
          overview: newOverview.value,
          openSettlement: none(),
          settlementExport: none()
        }
      : {
          ...s,
          overview: newOverview
        }
  },
  onExportUpdate: (
    updater: Action<AsyncState<SettlementSpecificationWithUserIds[]>>
  ): Action<State> => s =>
    s.kind !== 'overview' || s.settlementExport.v.kind === 'l'
      ? s
      : {
          ...s,
          settlementExport: {
            ...s.settlementExport,
            v: {
              ...s.settlementExport.v,
              v: {
                ...s.settlementExport.v.v,
                specifications: updater(s.settlementExport.v.v.specifications)
              }
            }
          }
        },

  onToggleSettlement: (
    updater: Action<Option<OpenSettlement>>
  ): Action<State> => s =>
    s.kind !== 'overview'
      ? s
      : {
          ...s,
          openSettlement: updater(s.openSettlement)
        },
  onPageChange: (updater: Action<number>): Action<State> => s =>
    s.kind !== 'overview' || updater(s.overview.page) === s.overview.page
      ? s
      : {
          kind: 'loading',
          overview: loadingAsyncState(_ =>
            getSettlements(updater(s.overview.page))
          )
        },
  onSettlementExport: (settlement: Settlement): Action<State> => s =>
    s.kind !== 'overview'
      ? s
      : {
          ...s,
          settlementExport: some({
            settlement,
            specifications: loadingAsyncState(_ =>
              getSettlementSpecifications(settlement)
            )
          })
        },
  stateUpdater: (updater: Action<State>): Action<State> => s => {
    const newState = updater(s)
    if (
      s.kind !== 'overview' ||
      newState.kind !== 'overview' ||
      s.settlementExport.v.kind !== 'r' ||
      newState.settlementExport.v.kind !== 'r' ||
      s.settlementExport.v.v.specifications.kind === 'loaded' ||
      newState.settlementExport.v.v.specifications.kind !== 'loaded'
    ) {
      return newState
    }

    exportSettlementSpecificationsToCsv(
      newState.settlementExport.v.v.settlement,
      newState.settlementExport.v.v.specifications.value
    )

    return {
      ...newState,
      settlementExport: none()
    }
  }
}
